import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ExternalLink, Gauge, PlugZap, RefreshCw, Unplug } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { fortnoxApi } from "@/lib/api";

export function FortnoxPanel({ notice }: { notice?: { ok?: boolean; error?: string } }) {
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ["fortnox", "status"],
    queryFn: () => fortnoxApi.status(false),
  });

  const test = useMutation({
    mutationFn: () => fortnoxApi.status(true),
    onSuccess: (result) => {
      queryClient.setQueryData(["fortnox", "status"], result);
      if (result.test_error) toast.error(result.test_error);
      else toast.success(`Kopplingen fungerar — ansluten till ${result.company_name ?? "Fortnox"}`);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const refreshBalances = useMutation({
    mutationFn: fortnoxApi.refreshBalances,
    onSuccess: (balances) => {
      queryClient.invalidateQueries({ queryKey: ["balances"] });
      toast.success(
        balances.length > 0
          ? `Flexsaldo hämtat för ${balances.length} anställd(a)`
          : "Ingen anställd har anställningsnummer ännu",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const disconnect = useMutation({
    mutationFn: fortnoxApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fortnox"] });
      toast.success("Kopplingen till Fortnox är bruten");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const data = status.data;

  return (
    <div className="surface space-y-5 p-5 sm:p-6">
      <div>
        <h2 className="text-lg font-semibold">Fortnox</h2>
        <p className="text-sm text-muted-foreground">
          Kopplingen görs en gång med ett tjänstekonto. Därefter förnyas den automatiskt och ingen
          behöver logga in i Fortnox igen.
        </p>
      </div>

      {notice?.ok && (
        <p className="flex items-center gap-2 rounded-2xl bg-success/12 px-4 py-3 text-sm text-success">
          <CheckCircle2 className="size-4 shrink-0" />
          Fortnox är nu anslutet.
        </p>
      )}
      {notice?.error && (
        <p className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Anslutningen misslyckades: {notice.error}
        </p>
      )}

      {status.isLoading ? (
        <p className="py-6 text-center text-muted-foreground">Hämtar status…</p>
      ) : status.isError ? (
        <p className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {(status.error as Error).message}
        </p>
      ) : (
        data && (
          <>
            <dl className="grid gap-3 rounded-2xl bg-muted p-4 text-sm sm:grid-cols-2">
              <Row label="Status">
                {data.connected ? (
                  <span className="font-semibold text-success">Ansluten</span>
                ) : (
                  <span className="font-semibold text-muted-foreground">Inte ansluten</span>
                )}
              </Row>
              <Row label="Bolag">{data.company_name ?? "—"}</Row>
              <Row label="Token giltigt till">{formatMoment(data.expires_at)}</Row>
              <Row label="Senast förnyat">{formatMoment(data.last_refreshed_at)}</Row>
              <Row label="Behörigheter">{data.scope ?? "—"}</Row>
              <Row label="Client ID / Secret">{data.has_credentials ? "Inlagda" : "Saknas"}</Row>
            </dl>

            {!data.has_credentials && (
              <p className="rounded-2xl bg-warning/15 px-4 py-3 text-sm text-warning-foreground">
                Lägg in <code>FORTNOX_CLIENT_ID</code> och <code>FORTNOX_CLIENT_SECRET</code> som
                hemligheter på Pages-projektet innan du ansluter.
              </p>
            )}

            <div className="space-y-2">
              <p className="text-sm font-medium">Redirect-URI att registrera i Fortnox</p>
              <code className="block overflow-x-auto rounded-2xl bg-muted px-4 py-3 text-xs">
                {data.redirect_uri}
              </code>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                className="rounded-full"
                disabled={!data.has_credentials}
                onClick={() => {
                  // OAuth kräver en riktig sidnavigering, inte fetch.
                  window.location.href = fortnoxApi.connectUrl();
                }}
              >
                <PlugZap className="size-4" />
                {data.connected ? "Anslut om" : "Anslut Fortnox"}
              </Button>

              <Button
                variant="secondary"
                className="rounded-full"
                disabled={!data.connected || test.isPending}
                onClick={() => test.mutate()}
              >
                <RefreshCw className="size-4" />
                {test.isPending ? "Testar…" : "Testa kopplingen"}
              </Button>

              <Button
                variant="secondary"
                className="rounded-full"
                disabled={!data.connected || refreshBalances.isPending}
                onClick={() => refreshBalances.mutate()}
              >
                <Gauge className="size-4" />
                {refreshBalances.isPending ? "Hämtar…" : "Uppdatera flexsaldon"}
              </Button>

              <Button
                variant="ghost"
                className="rounded-full text-destructive"
                disabled={!data.connected || disconnect.isPending}
                onClick={() => {
                  if (confirm("Bryta kopplingen till Fortnox?")) disconnect.mutate();
                }}
              >
                <Unplug className="size-4" /> Koppla från
              </Button>

              <a
                href="https://developer.fortnox.se/"
                target="_blank"
                rel="noreferrer"
                className="ml-auto inline-flex items-center gap-1.5 self-center text-sm text-muted-foreground hover:underline"
              >
                Fortnox utvecklarportal <ExternalLink className="size-3.5" />
              </a>
            </div>

            <p className="text-xs text-muted-foreground">
              Kopplingen upphör om den inte används på 45 dagar. Skickar du löneunderlag varje månad
              håller den sig vid liv av sig själv.
            </p>
          </>
        )
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground uppercase">{label}</dt>
      <dd className="mt-0.5 break-words">{children}</dd>
    </div>
  );
}

function formatMoment(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
}
